import { useSelector } from 'react-redux'
import {
  CaseCategory,
  CaseListItem,
  mapDraftToTaskBody,
  MessageIdentifier,
  useAddCase,
  useTaskCollectionPreviewMutation,
  useUpdateCase,
} from '~/entities/case'
import { CaseOrCategory, isCase } from './TriageCaseOrCategorySelect/types'
import { TaskFormDraft } from '~/entities/task'
import { format } from 'date-fns'
import { ISO_DATE_FORMAT, setDueDateToEOD } from '~/shared/lib/date'
import type { MeState } from '~/app/store/reducers/me'
import { v4 as uuidv4 } from 'uuid'
import {
  chatAttachment,
  chatV2,
  ChatV2CreateBodyParams,
  documentsProvidersPatientsFiles,
  ProvidersCasesCreateBodyParams,
  ProvidersCasesCreateResponse,
  ProvidersCasesPartialUpdateBodyParams,
  ProvidersCasesPartialUpdateResponse,
} from '@fireflyhealth/api'
import { FileWithType } from '~/shared/ui'
import { TranscriptMessage } from './selection/clipboard'
import { getPubnubChannelForThread } from '../chatUtils'
import { logger } from '~/shared/lib/logger'
import invariant from 'tiny-invariant'
import { useMutation } from '~/shared/hooks/query'
import { fireAndForget } from '~/shared/lib/fireAndForget'

export const findMessageIdentifier = (message: { id: number; kind?: string }) => {
  return {
    id: message.id,
    type:
      // Note: This handles both chat v2 and v3 messages.
      message.kind === 'phonecall' || message.kind === 'phone' ? 'phonecall' : 'chatmessagev2',
  } as MessageIdentifier
}

export const messageDateInfo = (timestamp: string | number) => {
  // Format timestamp
  const date = new Date(timestamp)
  const messageDate = format(date, "EEE. MMM. d, yyyy 'at' h:mm aaa")
  // Add a hover title for specific date
  const preciseDate = format(date, 'EEEE, MMMM do yyyy, h:mm:ss aaa')
  return { preciseDate, messageDate }
}

// Snake_case message→case relation for the generated write body.
const getMessageCaseRelation = (messageIdentifier: MessageIdentifier) => ({
  content_type: messageIdentifier.type,
  object_id: messageIdentifier.id,
})

// Maps an existing snake_case case task to the generated partial-update body,
// preserving the task id so the backend updates rather than recreates it.
const mapCaseTaskToBody = (task: NonNullable<CaseListItem['tasks']>[number]) => ({
  id: task.id,
  title: task.title ?? '',
  owner_group: task.owner_group ?? undefined,
  priority: task.priority ?? 0,
  is_complete: task.is_complete ?? false,
  ...(task.due_date ? { due_date: setDueDateToEOD(task.due_date) } : {}),
})

/**
 * Creates a case from a category with optional message relation
 */
export const createCaseFromCategory = async (
  category: CaseOrCategory,
  person: { id: number; user: { id: number } },
  me: Pick<MeState, 'id' | 'assignee_group'>,
  taskCollectionPreviewMutation: ReturnType<typeof useTaskCollectionPreviewMutation>,
  handleAddPatientCase: (
    body: ProvidersCasesCreateBodyParams,
  ) => Promise<ProvidersCasesCreateResponse>,
  messageIdentifier?: MessageIdentifier,
  externalDescription?: string,
  options?: { linkMessageToCase?: boolean },
): Promise<ProvidersCasesCreateResponse> => {
  if (isCase(category)) {
    throw new Error('Expected category, got case')
  }

  // Create tasks from task collection if available
  let tasks: TaskFormDraft[] = []
  if (category?.task_collection_id && me.id && me.assignee_group?.id) {
    const taskData = await taskCollectionPreviewMutation.mutateAsync({
      caseCategoryId: category.id,
      userId: person.user.id,
      meAssigneeGroup: me.assignee_group.id,
    })

    if (taskData) tasks = taskData.taskPreviews
  }

  // Create message relation if provided (can be disabled via options)
  let relations: { content_type: string; object_id: number }[] = []
  const linkMessage = options?.linkMessageToCase ?? true
  if (messageIdentifier && linkMessage) {
    relations = [getMessageCaseRelation(messageIdentifier)]
  }

  const dueDate = format(new Date(), ISO_DATE_FORMAT)
  const ownerGroup = me.assignee_group?.id

  const body: ProvidersCasesCreateBodyParams = {
    person: person.id,
    category: category.id,
    description: category?.description || '',
    notes: category?.notes || '',
    relations,
    tasks: tasks.map(task => mapDraftToTaskBody(task, setDueDateToEOD(task.due_date))),
    ...(externalDescription !== undefined ? { external_description: externalDescription } : {}),
    ...(dueDate ? { due_date: setDueDateToEOD(dueDate) } : {}),
    ...(ownerGroup !== undefined ? { owner_group: ownerGroup } : {}),
  }

  return handleAddPatientCase(body)
}

type SendChatPayload = {
  text: string
  file: FileWithType | null
  selectedCategory: CaseCategory | null
  focusedCase: { id: number; external_description?: string | null } | null
  externalDescription: string | undefined
  quotedMessages: TranscriptMessage[] | null
}

const uploadAttachment = async (file: FileWithType, patientId: number): Promise<number> => {
  const data = new FormData()
  data.append('file', file.data)
  if (file.type === 'document') {
    data.append('title', file.data.name)
    const { data: res } = await documentsProvidersPatientsFiles.create(
      { patient: patientId.toString(), provider: 'me' },
      { data },
    )
    return res.id
  } else {
    const { data: res } = await chatAttachment.create({ data })
    return res.id
  }
}

type UseSendChatMutationOptions = {
  patient: { id: number | null; person: { id: number } }
  onCaseCreated?: (caseId: number) => void
  onMessagePosted?: (data: ChatV2CreateBodyParams) => void
  onSuccess?: (caseId: number) => void | Promise<void>
  onError?: () => void | Promise<void>
}

export const useSendChatMutation = ({
  patient,
  onCaseCreated,
  onMessagePosted,
  onSuccess,
  onError,
}: UseSendChatMutationOptions) => {
  const me = useSelector(state => state.me)
  const pubnub = useSelector(state => state.chat.pubnub)

  const { mutateAsync: handleAddPatientCase } = useAddCase()
  const { mutateAsync: handleUpdateCase } = useUpdateCase()
  const taskCollectionPreviewMutation = useTaskCollectionPreviewMutation()
  return useMutation(
    async (payload: SendChatPayload) => {
      invariant(pubnub, 'PubNub client not available')
      invariant(patient.id, 'No patient ID available')

      // 1. Upload attachment if file exists
      let attachment: number | null = null
      let fileAttachment: number | null = null
      if (payload.file) {
        const attachmentId = await uploadAttachment(payload.file, patient.id)
        if (payload.file.type === 'image') {
          attachment = attachmentId
        } else {
          fileAttachment = attachmentId
        }
      }

      // 2. Create case if a category is selected, or update case if needed
      let caseId: number | undefined = undefined
      if (payload.selectedCategory) {
        const createdCase = await createCaseFromCategory(
          payload.selectedCategory,
          { id: patient.person.id, user: { id: patient.id } },
          me,
          taskCollectionPreviewMutation,
          handleAddPatientCase,
          undefined,
          payload.externalDescription,
        )
        onCaseCreated?.(createdCase.id)
        caseId = createdCase.id
      } else if (payload.focusedCase) {
        if (payload.externalDescription && !payload.focusedCase.external_description) {
          await handleUpdateCase({
            id: payload.focusedCase.id,
            body: { external_description: payload.externalDescription },
          })
        }
        caseId = payload.focusedCase.id
      }

      invariant(caseId, 'No case ID provided')
      invariant(me.id !== null, 'No sender ID available')

      const relations = payload.quotedMessages?.map(m => ({
        content_type: m.content_type,
        object_id: m.object_id,
      }))

      // `metadata` is typed `unknown`, which PubNub cannot publish; this client never sets it.
      const messageData: Omit<ChatV2CreateBodyParams, 'metadata'> = {
        attachment,
        file_attachment: fileAttachment,
        text: payload.text.trim(),
        uid: uuidv4(),
        sender: me.id,
        case_id: caseId,
        relations: relations?.length ? relations : undefined,
      }

      // 3. Notify caller for optimistic cache updates
      onMessagePosted?.(messageData)

      // 4. POST message to API via generated client
      const { data } = await chatV2.create({ data: messageData })

      // Since all messages supply a case ID, a thread should always be returned.
      invariant(data.thread, 'No thread ID in message response')

      // 5. Publish to PubNub for real-time delivery
      const publishResponse = await pubnub.publish({
        message: messageData,
        channel: getPubnubChannelForThread(data.thread),
      })
      logger.info('In sendChat, PubNub publish successful', publishResponse)

      // 6. Notify caller for final cache invalidation
      await onSuccess?.(caseId)

      return
    },
    {
      onError: onError
        ? () => fireAndForget('[Chat] sendChat onError', async () => onError())
        : undefined,
    },
    { error: 'Failed to send chat message' },
  )
}

/**
 * Links an existing case to a message
 */
export const linkCaseToMessage = async (
  caseObj: CaseListItem,
  messageIdentifier: MessageIdentifier,
  handleUpdatePatientCase: (args: {
    id: number
    body: ProvidersCasesPartialUpdateBodyParams
  }) => Promise<ProvidersCasesPartialUpdateResponse>,
): Promise<ProvidersCasesPartialUpdateResponse> => {
  const relations = [
    ...(caseObj.relations ?? []).map(relation => ({
      content_type: relation.content_type,
      object_id: relation.object_id,
    })),
    getMessageCaseRelation(messageIdentifier),
  ]

  const body: ProvidersCasesPartialUpdateBodyParams = {
    ...(caseObj.action != null ? { action: caseObj.action } : {}),
    ...(caseObj.owner_group != null ? { owner_group: caseObj.owner_group } : {}),
    person: caseObj.person ?? null,
    category: caseObj.category,
    description: caseObj.description ?? '',
    ...(caseObj.external_description !== undefined
      ? { external_description: caseObj.external_description }
      : {}),
    ...(caseObj.notes !== undefined ? { notes: caseObj.notes } : {}),
    ...(caseObj.due_date ? { due_date: setDueDateToEOD(caseObj.due_date) } : {}),
    tasks: (caseObj.tasks ?? []).map(mapCaseTaskToBody),
    relations,
  }

  return handleUpdatePatientCase({ id: caseObj.id, body })
}
